import api, { setAccessToken, scheduleTokenRefresh } from '@/shared/api/client'
import { getDeviceFingerprint } from '@/shared/utils/device-fingerprint'
import type { AuthUser } from '@/shared/stores/auth.store'

// ── Response types ──

export interface DisplacedSession {
  device_label: string
  last_active_at: string | null
}

export interface ActiveSession {
  device_label: string
  last_active_at: string | null
}

export interface LoginResponse {
  status:
    | 'authenticated'
    | 'mfa_required'
    | 'mfa_enrollment_required'
    | 'email_otp_required'
    | 'active_session_exists'
  access_token?: string
  expires_in?: number
  user?: AuthUser
  mfa_challenge_token?: string
  mfa_challenge_expires_at?: string
  mfa_enrolled?: boolean
  // Email OTP branch (present only when status === 'email_otp_required').
  email_otp_challenge_token?: string
  email_otp_expires_at?: string
  masked_email?: string
  session_displaced?: boolean
  displaced_session?: DisplacedSession
  active_session?: ActiveSession
}

export type MfaVerifyResponse =
  | {
      status: 'active_session_exists'
      active_session: ActiveSession
    }
  | {
      access_token: string
      expires_in: number
      user?: AuthUser
      session_displaced?: boolean
      displaced_session?: DisplacedSession
    }

// Email-OTP verify returns the same shape as MFA verify — either the
// active-session gate or the full authenticated token package.
export type EmailOtpVerifyResponse = MfaVerifyResponse

export interface MfaSetupResponse {
  totp_secret: string
  totp_uri: string
  recovery_codes: string[]
}

export type MfaSetupConfirmResponse =
  | {
      status: 'active_session_exists'
      active_session: ActiveSession
    }
  | {
      access_token: string
      expires_in: number
      user?: AuthUser
      session_displaced?: boolean
      displaced_session?: DisplacedSession
    }

export function hasAuthTokens(
  res: MfaVerifyResponse | MfaSetupConfirmResponse | MfaRecoveryResponse | EmailOtpVerifyResponse,
): res is {
  access_token: string
  expires_in: number
  user?: AuthUser
  session_displaced?: boolean
  displaced_session?: DisplacedSession
  recovery_codes_remaining?: number
} {
  return !('status' in res && res.status === 'active_session_exists')
}

export type MfaRecoveryResponse =
  | {
      status: 'active_session_exists'
      active_session: ActiveSession
    }
  | {
      access_token: string
      expires_in: number
      recovery_codes_remaining: number
      user?: AuthUser
      session_displaced?: boolean
      displaced_session?: DisplacedSession
    }

// ── Centralized auth API calls ──

export const authApi = {
  login: async (email: string, password: string, forceLogin = false) => {
    const fp = await getDeviceFingerprint()
    return api.post<LoginResponse>(
      '/auth/login',
      { email, password, ...(forceLogin ? { force_login: true } : {}) },
      { headers: { 'X-Device-Fingerprint': fp } },
    ).then((r) => r.data)
  },

  mfaVerify: async (mfa_challenge_token: string, code: string, forceLogin = false) => {
    const fp = await getDeviceFingerprint()
    return api.post<MfaVerifyResponse>(
      '/auth/mfa-verify',
      { mfa_challenge_token, code, ...(forceLogin ? { force_login: true } : {}) },
      { headers: { 'X-Device-Fingerprint': fp } },
    ).then((r) => r.data)
  },

  emailOtpVerify: async (
    email_otp_challenge_token: string,
    code: string,
    forceLogin = false,
  ) => {
    const fp = await getDeviceFingerprint()
    return api.post<EmailOtpVerifyResponse>(
      '/auth/email-otp-verify',
      { email_otp_challenge_token, code, ...(forceLogin ? { force_login: true } : {}) },
      { headers: { 'X-Device-Fingerprint': fp } },
    ).then((r) => r.data)
  },

  emailOtpResend: (email_otp_challenge_token: string) =>
    api.post<{
      email_otp_challenge_token: string
      email_otp_expires_at: string
      masked_email: string
    }>('/auth/email-otp-resend', { email_otp_challenge_token }).then((r) => r.data),

  mfaSetup: (mfa_challenge_token: string) =>
    api.post<MfaSetupResponse>('/auth/mfa-setup', { mfa_challenge_token }).then((r) => r.data),

  mfaSetupConfirm: async (mfa_challenge_token: string, code: string, forceLogin = false) => {
    const fp = await getDeviceFingerprint()
    return api.post<MfaSetupConfirmResponse>(
      '/auth/mfa-setup-confirm',
      { mfa_challenge_token, code, ...(forceLogin ? { force_login: true } : {}) },
      { headers: { 'X-Device-Fingerprint': fp } },
    ).then((r) => r.data)
  },

  mfaRecovery: async (mfa_challenge_token: string, recovery_code: string, forceLogin = false) => {
    const fp = await getDeviceFingerprint()
    return api.post<MfaRecoveryResponse>(
      '/auth/mfa-recovery',
      { mfa_challenge_token, recovery_code, ...(forceLogin ? { force_login: true } : {}) },
      { headers: { 'X-Device-Fingerprint': fp } },
    ).then((r) => r.data)
  },

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (reset_token: string, new_password: string) =>
    api.post('/auth/reset-password', { reset_token, new_password }),

  me: () =>
    api.get<{ data: AuthUser }>('/auth/me').then((r) => r.data),

  logout: () =>
    api.post('/auth/logout').catch(() => { /* best-effort */ }),

  switchClient: (client_id: string) =>
    api.post('/auth/switch-client', { client_id }).then((r) => r.data),

  // MCP browser-login: mint a one-time code for a local MCP client (loopback +
  // PKCE). Called after a normal login when the page was opened in MCP mode.
  mcpAuthorize: (body: { redirect_uri: string; state: string; code_challenge: string }) =>
    api
      .post<{ code: string; expires_in: number }>('/auth/mcp/authorize', {
        ...body,
        code_challenge_method: 'S256',
      })
      .then((r) => r.data),
}

/**
 * Store access token in memory and schedule proactive refresh.
 * Called after successful login, MFA verify, etc.
 */
export function handleAuthToken(accessToken: string, expiresIn = 900) {
  setAccessToken(accessToken)
  scheduleTokenRefresh(expiresIn)
}
